// stores/dashboardStore.ts — modular dashboard layout store (design §3, §9.2).
//
// Holds ONLY durable layout metadata (layouts, grids, panel configs, sync-group
// membership, active layout). No volatile state — no crosshair index, visible
// range, or candle data (that lives in panelDataStore).
//
// Persistence is crash-safe (M7, §9.2.1):
//  - partialize persists ONLY `activeLayout` + custom (isPreset:false) layouts.
//    Preset grids are NEVER persisted → preset code updates always ship and the
//    localStorage quota can't be blown by preset bloat.
//  - merge is fully try/catch-wrapped: any malformed/quota-truncated blob falls
//    back to the in-code PRESET_LAYOUTS instead of white-screening; each custom
//    is shape-validated and dropped (warn, non-fatal) if malformed.
//  - onRehydrateStorage nukes the persisted key on rehydrate error so the next
//    load starts clean.
//
// Storage key is 'lynx-dashboard' (Phase-1 acceptance #3 contract).

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LayoutDefinition } from '../pages/chart/modular/dashboardTypes';
import type { DashboardData, DashboardStore } from './dashboardStore.types';
import { PRESET_LAYOUTS } from '../pages/chart/layouts/presets';
import { createDashboardActions } from './dashboardActions';
import { dashboardJSONStorage } from './dashboardStorage';

const STORAGE_KEY = 'lynx-dashboard';

/** True only for a well-formed user custom layout we are willing to restore. */
function isValidCustomLayout(l: unknown): l is LayoutDefinition {
  if (!l || typeof l !== 'object') return false;
  const o = l as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.name === 'string' &&
    Array.isArray(o.grid) &&
    !!o.panelConfigs &&
    typeof o.panelConfigs === 'object' &&
    !Array.isArray(o.panelConfigs) &&
    o.isPreset === false
  );
}

/** Fresh, in-code default state: presets seeded from source, 'trading' active. */
function freshState(): {
  activeLayout: string;
  layouts: Record<string, LayoutDefinition>;
  lockLayout: boolean;
} {
  return {
    activeLayout: 'trading',
    layouts: structuredClone(PRESET_LAYOUTS),
    lockLayout: false,
  };
}

/** Best-effort wipe of the persisted blob (#13). Non-fatal in SSR / node tests. */
function clearPersistedDashboard(): void {
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('[dashboardStore] could not clear localStorage', e);
  }
}

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      ...freshState(),
      ...createDashboardActions(set, get),

      // Factory reset (#13): re-seed presets + drop customs + force lock off in
      // one set(), then nuke the persisted blob so a stale custom (e.g. a removed
      // data-integrity panel ref) can't re-hydrate on the next load. freshState()
      // guarantees lockLayout:false, clearing any stuck view-only lock.
      resetAllLayouts: () => {
        set(freshState());
        clearPersistedDashboard();
      },
    }),
    {
      name: STORAGE_KEY,
      version: 2,

      // v<2 blobs (pre-rework shapes, removed-panel refs) are dropped wholesale:
      // return fresh default state so nothing stale survives the upgrade. The
      // partialize/merge path then re-seeds presets from code as usual. v>=2
      // blobs pass through untouched (merge still shape-validates each custom).
      migrate: (persisted, version) =>
        version < 2 ? freshState() : (persisted as Partial<DashboardData>),

      // Quota-hardened storage engine (§9.2 / §11 risk 6): on QuotaExceededError
      // it lazily evicts the oldest custom layout and retries, instead of letting
      // the write throw out of set() and white-screen the dashboard.
      storage: dashboardJSONStorage,

      // Persist ONLY the active layout id + user customs + the view-only lock.
      // Presets re-seed from code every load (M7 / §9.2.1) — never persist preset
      // grids.
      partialize: (s) => ({
        activeLayout: s.activeLayout,
        lockLayout: s.lockLayout,
        layouts: Object.fromEntries(
          Object.entries(s.layouts).filter(([, l]) => !l.isPreset),
        ),
      }),

      merge: (persisted, current) => {
        try {
          const p = (persisted ?? {}) as Partial<DashboardStore>;
          const customs: Record<string, LayoutDefinition> = {};
          if (p.layouts && typeof p.layouts === 'object' && !Array.isArray(p.layouts)) {
            for (const [id, l] of Object.entries(p.layouts)) {
              if (isValidCustomLayout(l)) customs[id] = l;
              else
                console.warn(
                  `[dashboardStore] dropping malformed custom layout "${id}"`,
                );
            }
          }
          const layouts = { ...structuredClone(PRESET_LAYOUTS), ...customs };
          const activeLayout =
            typeof p.activeLayout === 'string' && layouts[p.activeLayout]
              ? p.activeLayout
              : 'trading';
          const lockLayout =
            typeof p.lockLayout === 'boolean' ? p.lockLayout : false;
          return { ...current, activeLayout, layouts, lockLayout };
        } catch (err) {
          console.error('[dashboardStore] merge threw; seeding fresh presets', err);
          // `current` already carries structuredClone(PRESET_LAYOUTS) from init.
          return current;
        }
      },

      onRehydrateStorage: () => (_state, error) => {
        if (!error) return;
        console.error('[dashboardStore] rehydrate failed; nuking persisted key', error);
        try {
          window.localStorage?.removeItem(STORAGE_KEY);
        } catch (e) {
          console.error('[dashboardStore] could not clear localStorage', e);
        }
      },
    },
  ),
);
